import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, History, ChevronRight, ChevronDown, Info, Pencil,
} from "lucide-react";
import MOTDefectDecisionRow from "./MOTDefectDecisionRow";

/**
 * MOT Outcome Summary (MOT-2)
 * --------------------------------------------------------------------------
 * Read-only render of a submitted outcome + classified defects + continuity
 * hints. Tester wording is preserved verbatim. Classifier-derived metadata
 * (system_group, location, component) renders as muted chips, never
 * replaces the tester's text.
 *
 * Strictly read-only by contract. The "Revise" button is the ONLY way to
 * change the outcome — and even then, the original body is preserved
 * server-side under `revisions[]`.
 */
export default function MOTOutcomeSummary({
  outcome,
  summary,
  continuityHints,
  decisionSummary,
  sessionId,
  onRevise,
  onDecisionChange,
  // testerView=true strips the FOH-only surfaces (per-defect decision
  // row + continuity hints handoff) so the tester workspace stays
  // focussed on what the tester actually owns: outcome + defects +
  // revise. Same component still serves FOH elsewhere by leaving
  // testerView false.
  testerView = false,
}) {
  const [showRevisions, setShowRevisions] = useState(false);

  if (!outcome) return null;

  const isFail = outcome.test_result === "FAILED";
  const counts = outcome.counts || summary?.counts || {};
  const defectsBySeverity = groupBySeverity(outcome.defects || []);
  const terminalItems = continuityHints?.terminal_items || [];
  const advisoryItems = continuityHints?.advisory_items || [];
  const revisions = outcome.revisions || [];
  const byIndex = decisionSummary?.by_index || {};
  const decisionCounts = decisionSummary?.counts || {};
  const unactionedCount = decisionCounts.unactioned ?? 0;

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="mot-outcome-summary"
      data-result={outcome.test_result}
    >
      {/* Header — verdict + key facts */}
      <header
        className={`px-6 pt-5 pb-4 border-b border-slate-100 ${
          isFail ? "bg-red-50/40" : "bg-emerald-50/30"
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <ShieldCheck className={`w-5 h-5 ${isFail ? "text-red-700" : "text-emerald-700"}`} />
              <p className="text-[15px] uppercase tracking-[0.18em] text-slate-600 font-semibold">
                MOT outcome
              </p>
            </div>
            <p
              className={`text-[36px] font-bold leading-none ${
                isFail ? "text-red-700" : "text-emerald-700"
              }`}
              data-testid="mot-outcome-result-badge"
            >
              {outcome.test_result}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[15px] text-slate-700">
              {outcome.test_completed_at && (
                <span data-testid="mot-outcome-completed">
                  Tested {formatDateTime(outcome.test_completed_at)}
                </span>
              )}
              {outcome.odometer_reading != null && (
                <span data-testid="mot-outcome-odometer-display">
                  {outcome.odometer_reading.toLocaleString()} {outcome.odometer_unit || "mi"}
                </span>
              )}
              {outcome.mot_expiry_date && (
                <span data-testid="mot-outcome-expiry-display">
                  Expires {formatDate(outcome.mot_expiry_date)}
                </span>
              )}
              {outcome.test_number && (
                <span data-testid="mot-outcome-test-number-display" className="text-slate-400">
                  · Test #{outcome.test_number}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <SeverityChip count={counts.dangerous} label="Dangerous" tone="red-700" />
              <SeverityChip count={counts.major} label="Major" tone="red-600" />
              <SeverityChip count={counts.minor} label="Minor" tone="amber-700" />
              <SeverityChip count={counts.advisory} label="Advisory" tone="slate-600" />
              {/* MOT-3 — FOH decision status at a glance. Hidden in
                  tester mode (testers don't track FOH decisions). */}
              {!testerView && decisionSummary && (decisionCounts.actionable ?? 0) > 0 && (
                <span
                  className={`text-[11.5px] uppercase tracking-wide font-semibold border rounded-full px-2.5 py-0.5 ${
                    unactionedCount > 0
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-emerald-50 text-emerald-800 border-emerald-200"
                  }`}
                  data-testid="mot-outcome-foh-decision-status"
                >
                  {unactionedCount > 0
                    ? `${unactionedCount} awaiting decision`
                    : "All decisions recorded"}
                </span>
              )}
            </div>
          </div>
          {onRevise && (
            <div className="flex flex-col items-stretch sm:items-end gap-2 flex-shrink-0">
              {/* "Create Yestimate" removed — MOT defects auto-import to
                  the FOH pricing screen via the background DVSA poller +
                  MOT auto-quote bridge. Tester does not need to push. */}
              <Button
                variant="outline"
                onClick={onRevise}
                className="text-[15px] min-h-[44px]"
                data-testid="mot-outcome-revise-btn"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Record revision
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="px-6 py-5 space-y-6">
        {/* Compliance disclaimer */}
        <p
          className="text-[14px] italic text-slate-600 leading-relaxed flex items-start gap-2"
          data-testid="mot-outcome-disclaimer"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" />
          Tester-owned. Original wording preserved verbatim. Severity is the tester's call — never inferred by the system.
        </p>

        {/* Tester notes */}
        {outcome.tester_notes && (
          <div data-testid="mot-outcome-notes-block">
            <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-1.5">
              Tester notes
            </p>
            <p className="text-[16px] text-slate-800 leading-relaxed italic">
              "{outcome.tester_notes}"
            </p>
          </div>
        )}

        {/* Defects — grouped by severity. Minor and Advisory are
            visually merged into a single "Minor & Advisory" group per
            FOH spec — both render at the amber tier of urgency. */}
        {(outcome.defects || []).length > 0 && (
          <div data-testid="mot-outcome-defects-list">
            <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2.5">
              Defects recorded
            </p>
            <div className="space-y-3">
              {[
                { key: "dangerous", buckets: ["dangerous"] },
                { key: "major",     buckets: ["major"] },
                { key: "minor",     buckets: ["minor", "advisory"] },
                { key: "user_entered", buckets: ["user_entered"] },
              ].map(({ key, buckets }) => {
                const items = buckets.flatMap((b) => defectsBySeverity[b] || []);
                if (items.length === 0) return null;
                return (
                  <div key={key} data-testid={`mot-outcome-defects-${key}`}>
                    <p className={`text-[14px] uppercase tracking-wide font-bold mb-2 ${SEVERITY_TONE[key]}`}>
                      {SEVERITY_LABEL[key]} · {items.length}
                    </p>
                    <ul className="space-y-2">
                      {items.map((d, i) => {
                        const absIdx = d._absoluteIndex;
                        const currentDecision = byIndex[absIdx] || null;
                        const sev = (d.severity || "advisory").toLowerCase();
                        const isActionable = sev !== "user_entered";
                        return (
                          <li
                            key={i}
                            className={`rounded-md border border-slate-200 bg-white px-4 py-3 ${SEVERITY_BORDER[sev] || ""}`}
                            data-testid={`mot-outcome-defect-item-${sev}-${i}`}
                            data-defect-index={absIdx}
                          >
                            <p className="text-[16px] text-slate-900 leading-snug font-medium">
                              {d.text}
                            </p>
                            {(d.system_group || d.location || d.component) && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {d.system_group && d.system_group !== "other" && (
                                  <Chip>{d.system_group}</Chip>
                                )}
                                {d.location && d.location !== "unknown" && (
                                  <Chip>{d.location}</Chip>
                                )}
                                {d.component && <Chip>{d.component}</Chip>}
                              </div>
                            )}
                            {/* MOT-3 — per-defect FOH decision row.
                                Hidden when testerView=true (tester
                                workspace) — these actions belong to
                                FOH on the pricing screen, not the
                                tester at the ramp. */}
                            {!testerView && isActionable && sessionId && (
                              <MOTDefectDecisionRow
                                sessionId={sessionId}
                                outcomeId={outcome.id}
                                defectIndex={absIdx}
                                defect={d}
                                currentDecision={currentDecision}
                                onChange={onDecisionChange}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Continuity hints — FOH operational handoff. Hidden in
            tester mode (testers don't action these; FOH does). */}
        {!testerView && (terminalItems.length > 0 || advisoryItems.length > 0) && (
          <div data-testid="mot-outcome-continuity">
            <p className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
              Continuity hints — operational handoff
            </p>
            <p
              className="text-[12.5px] italic text-slate-500 leading-relaxed flex items-start gap-2 mb-2.5"
              data-testid="mot-outcome-continuity-disclaimer"
            >
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
              Hints only — no automatic pricing, no upsell. FOH decides what (if anything) becomes a yestimate line.
            </p>
            <div className="space-y-3">
              {terminalItems.length > 0 && (
                <ContinuityGroup
                  title="Stop-the-vehicle items"
                  items={terminalItems}
                  testid="continuity-terminal"
                />
              )}
              {advisoryItems.length > 0 && (
                <ContinuityGroup
                  title="Advisories / minor"
                  items={advisoryItems}
                  testid="continuity-advisory"
                />
              )}
            </div>
          </div>
        )}

        {/* Revisions */}
        {revisions.length > 0 && (
          <div
            className="border-t border-slate-100 pt-4"
            data-testid="mot-outcome-revisions"
          >
            <button
              type="button"
              onClick={() => setShowRevisions(!showRevisions)}
              className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1 hover:text-slate-800"
              data-testid="mot-outcome-revisions-toggle"
            >
              {showRevisions ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Revisions · {revisions.length}
            </button>
            {showRevisions && (
              <ul className="mt-3 space-y-2.5">
                {revisions.map((r, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-slate-200 bg-slate-50/50 px-3.5 py-2.5"
                    data-testid={`mot-outcome-revision-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[13px] text-slate-700">
                        <History className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-slate-400" />
                        <span className="font-semibold">{r.revised_by_name || "Tester"}</span> ·{" "}
                        {formatDateTime(r.revised_at)}
                      </p>
                    </div>
                    <p className="text-[13.5px] text-slate-800 italic mt-1.5">"{r.reason}"</p>
                    <p className="text-[12px] text-slate-400 mt-1">
                      Previous: {r.previous?.test_result} · {r.previous?.odometer_reading?.toLocaleString()}{" "}
                      {outcome.odometer_unit || "mi"} · {r.previous?.defects?.length || 0} defect(s)
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ContinuityGroup({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[12.5px] uppercase tracking-wide text-slate-600 font-semibold mb-1.5">
        {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className="rounded-md border border-slate-200 bg-white px-3.5 py-2.5"
            data-testid={`${testid}-item-${i}`}
          >
            <p className="text-[14px] text-slate-800 leading-snug">{it.text}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5 items-center">
              <span className="text-[11px] uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 font-semibold">
                {it.severity_label}
              </span>
              {it.system_group && it.system_group !== "other" && <Chip>{it.system_group}</Chip>}
              {it.location && it.location !== "unknown" && <Chip>{it.location}</Chip>}
            </div>
            {it.foh_hint && (
              <p className="text-[12.5px] text-slate-500 leading-snug mt-1.5 italic">
                {it.foh_hint}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SeverityChip({ count, label, tone }) {
  if (!count || count === 0) return null;
  return (
    <span
      className={`text-[14px] uppercase tracking-wide font-bold border-2 rounded-full px-3.5 py-1.5 ${SEVERITY_CHIP_CLASSES[tone] || SEVERITY_CHIP_CLASSES.default}`}
      data-testid={`severity-chip-${label.toLowerCase()}`}
    >
      {count} {label}
    </span>
  );
}

// Tailwind JIT-safe mapping — every class string is statically present so
// the purge step keeps them in the build.
const SEVERITY_CHIP_CLASSES = {
  "red-700":   "text-red-700 border-red-200 bg-red-50",
  "red-600":   "text-red-600 border-red-200 bg-red-50",
  "amber-700": "text-amber-700 border-amber-200 bg-amber-50",
  "slate-600": "text-slate-600 border-slate-200 bg-slate-50",
  default:     "text-slate-600 border-slate-200 bg-slate-50",
};

function Chip({ children }) {
  return (
    <span className="text-[13px] uppercase tracking-wide bg-slate-100 text-slate-700 rounded px-2 py-1 font-semibold">
      {children}
    </span>
  );
}

const SEVERITY_ORDER = ["dangerous", "major", "minor", "advisory", "user_entered"];
const SEVERITY_LABEL = {
  dangerous: "Dangerous",
  major: "Major (failure)",
  minor: "Minor & Advisory",
  advisory: "Minor & Advisory",
  user_entered: "Tester note",
};
const SEVERITY_TONE = {
  dangerous: "text-slate-900",
  major: "text-red-700",
  minor: "text-amber-700",
  advisory: "text-amber-700",
  user_entered: "text-slate-500",
};
// Per-defect left-border colour. Matches the FOH pricing left rail
// (dangerous → black, major → red, minor & advisory → amber) so the
// tester review screen mirrors the rest of the system.
const SEVERITY_BORDER = {
  dangerous: "border-l-4 border-l-slate-900",
  major: "border-l-4 border-l-red-500",
  minor: "border-l-4 border-l-amber-500",
  advisory: "border-l-4 border-l-amber-500",
  user_entered: "border-l-4 border-l-slate-300",
};

function groupBySeverity(defects) {
  const out = {};
  for (let i = 0; i < defects.length; i++) {
    const d = defects[i];
    const sev = (d.severity || "advisory").toLowerCase();
    out[sev] = out[sev] || [];
    // Carry the original index alongside so the FE can target the
    // correct row when posting a decision.
    out[sev].push({ ...d, _absoluteIndex: i });
  }
  return out;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      + " · "
      + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
